import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { startAuthentication, startRegistration, browserSupportsWebAuthn, platformAuthenticatorIsAvailable } from '@simplewebauthn/browser';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { ScanFace, Loader2, Trash2, ShieldCheck, Pencil, Check, X, Camera } from 'lucide-react';
import { FaceEnrol, FaceSetupDialog, cameraSupported, faceDeviceReady } from './FaceScan';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cfg = () => ({ withCredentials: true });
const DEVICE_KEY = 'jmepl_passkey_device_registered';
const NEVER_KEY = 'jmepl_passkey_never_ask';
const PENDING_KEY = 'jmepl_passkey_prompt_pending';
const CRED_IDS_KEY = 'jmepl_passkey_cred_ids';

const knownCredIds = () => { try { return JSON.parse(localStorage.getItem(CRED_IDS_KEY) || '[]'); } catch (e) { return []; } };
const rememberCredId = (id) => {
  if (!id) return;
  const ids = [id, ...knownCredIds().filter((x) => x !== id)].slice(0, 10);
  localStorage.setItem(CRED_IDS_KEY, JSON.stringify(ids));
};
const forgetCredId = (id) => localStorage.setItem(CRED_IDS_KEY, JSON.stringify(knownCredIds().filter((x) => x !== id)));

/** Call right after a successful PASSWORD login so the next page can offer Face ID setup once. */
export const markPasskeyPromptPending = (store) => {
  try { sessionStorage.setItem(PENDING_KEY, store); } catch (e) { console.debug(e); }
  window.dispatchEvent(new CustomEvent('jmepl:passkey-prompt', { detail: store }));
};

const runRegistration = async (nickname = '') => {
  const { data } = await axios.post(`${API}/webauthn/registration/options`, {}, cfg());
  const { transactionId, ...optionsJSON } = data;
  let credential;
  try {
    credential = await startRegistration({ optionsJSON });
  } catch (err) {
    if (err?.name === 'InvalidStateError') localStorage.setItem(DEVICE_KEY, '1');
    throw err;
  }
  await axios.post(`${API}/webauthn/registration/verify`, { transactionId, credential, nickname }, cfg());
  localStorage.setItem(DEVICE_KEY, '1');
  rememberCredId(credential.id);
};

const snoozed = () => { const t = Number(localStorage.getItem(NEVER_KEY) || 0); return t > Date.now(); };

// Does THIS device have a built-in Face ID / Touch ID / Windows Hello / fingerprint? (null = still checking)
let platformAvail = null;
const platformReady = browserSupportsWebAuthn()
  ? platformAuthenticatorIsAvailable().then((v) => { platformAvail = !!v; return platformAvail; }).catch(() => { platformAvail = false; return false; })
  : Promise.resolve((platformAvail = false));
const usePlatformAuth = () => {
  const [avail, setAvail] = useState(platformAvail);
  useEffect(() => { let on = true; platformReady.then((v) => { if (on) setAvail(v); }); return () => { on = false; }; }, []);
  return avail;
};
const NO_PLATFORM_MSG = 'This computer has no built-in Face ID / Touch ID / Windows Hello, so face or fingerprint login is not possible here. Please sign in with your password on this device. Face login works on your iPhone, iPad, Touch ID MacBook and Windows Hello PC.';

const NicknameInput = ({ value, onChange, testid }) => (
  <input value={value} onChange={(e) => onChange(e.target.value)} maxLength={40} data-testid={testid}
    placeholder="Device name (e.g. Ankush iPhone) — optional"
    className="w-full h-9 px-3 text-sm border rounded-md border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-500" />
);

/** One registered device row with inline rename. */
const CredRow = ({ c, onRemove, onRenamed }) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(c.nickname || '');
  const save = async () => {
    if (!name.trim()) return;
    try {
      await axios.put(`${API}/webauthn/credentials/${c.credential_id}/nickname`, { nickname: name.trim() }, cfg());
      toast.success('Device name saved');
      setEditing(false);
      onRenamed();
    } catch (err) { toast.error(friendly(err)); }
  };
  return (
    <div className="flex items-center justify-between text-xs border rounded px-2 py-1.5 mb-1" data-testid={`passkey-cred-${c.credential_id}`}>
      <div className="truncate flex-1 min-w-0">
        {editing ? (
          <div className="flex items-center gap-1">
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} autoFocus data-testid={`passkey-rename-input-${c.credential_id}`}
              onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
              className="h-7 px-2 text-xs border rounded border-teal-400 flex-1 min-w-0 focus:outline-none" />
            <button onClick={save} className="text-teal-600" data-testid={`passkey-rename-save-${c.credential_id}`}><Check className="w-3.5 h-3.5" /></button>
            <button onClick={() => setEditing(false)} className="text-slate-400"><X className="w-3.5 h-3.5" /></button>
          </div>
        ) : (
          <p className="font-medium text-slate-700 truncate flex items-center gap-1" title={c.label}>
            <span data-testid={`passkey-nickname-${c.credential_id}`}>{c.nickname || 'Device'}</span>
            <button onClick={() => setEditing(true)} className="text-slate-400 hover:text-teal-600" title="Rename" data-testid={`passkey-rename-${c.credential_id}`}><Pencil className="w-3 h-3" /></button>
          </p>
        )}
        <p className="text-slate-400">Added {String(c.created_at).slice(0, 10)}{c.last_used_at ? ` · Last used ${String(c.last_used_at).slice(0, 10)}` : ''}</p>
        {c.needs_reregister && <p className="text-red-600 font-medium" data-testid={`passkey-stale-${c.credential_id}`}>Registered on the old web address — remove this device and register again</p>}
      </div>
      <button onClick={() => onRemove(c.credential_id)} className="text-red-500 ml-2" data-testid={`passkey-remove-${c.credential_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
    </div>
  );
};

const friendly = (err) => {
  if (err?.name === 'NotAllowedError') return platformAvail === false ? NO_PLATFORM_MSG : 'Face / device verification was cancelled or timed out';
  if (err?.name === 'InvalidStateError') return 'This device is already registered — use the "Sign in with Face ID" button on the login page';
  if (err?.name === 'NotSupportedError') return 'Face ID / Windows Hello is not supported on this device or browser';
  return err?.response?.data?.detail || err?.message || 'Passkey error';
};

const AUTO_SKIP_KEY = 'jmepl_skip_auto_face';
let autoFaceFired = false;

/** Login page: one-tap Face ID / Windows Hello / fingerprint login (staff + customer portal). */
export const PasskeyLoginButton = ({ onSuccess, className = '', testid = 'passkey-login-btn', onNeedsSetup, auto = false }) => {
  const [busy, setBusy] = useState(false);
  const [autoRan, setAutoRan] = useState(false);
  const [registered, setRegistered] = useState(() => !!(localStorage.getItem(DEVICE_KEY) || knownCredIds().length));
  const platform = usePlatformAuth();
  const supported = browserSupportsWebAuthn() && platform !== false;
  const login = useCallback(async (silent = false) => {
    if (!registered) {
      if (silent) return;
      toast.info('Face login is not set up on this device yet. Sign in with your password — you will be offered Face ID setup right away, and next time you can sign in with your face directly.', { duration: 8000 });
      onNeedsSetup?.();
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/webauthn/authentication/options`, { credentialIds: knownCredIds() }, cfg());
      const { transactionId, ...optionsJSON } = data;
      const ceremony = startAuthentication({ optionsJSON });
      const credential = silent
        ? await Promise.race([ceremony, new Promise((_, rej) => setTimeout(() => rej(Object.assign(new Error('timeout'), { name: 'NotAllowedError' })), 45000))])
        : await ceremony;
      const res = await axios.post(`${API}/webauthn/authentication/verify`, { transactionId, credential }, cfg());
      rememberCredId(credential.id);
      onSuccess(res.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        forgetCredId((() => { try { return JSON.parse(err.config?.data)?.credential?.id; } catch (e) { return null; } })());
        if (!knownCredIds().length) { localStorage.removeItem(DEVICE_KEY); setRegistered(false); }
      }
      if (!(silent && err?.name === 'NotAllowedError')) toast.error(friendly(err));
    } finally { setBusy(false); }
  }, [registered, onSuccess, onNeedsSetup]);

  const loginRef = React.useRef(login);
  loginRef.current = login;
  useEffect(() => {
    if (!auto || !supported || !registered || autoFaceFired || platform !== true) return;
    autoFaceFired = true;
    if (sessionStorage.getItem(AUTO_SKIP_KEY)) { sessionStorage.removeItem(AUTO_SKIP_KEY); return; }
    setTimeout(() => { setAutoRan(true); loginRef.current(true); }, 700);
  }, [auto, supported, registered, platform]);

  if (platform === false) {
    return (
      <p className="text-[11px] text-center opacity-80 px-2" data-testid={`${testid}-unavailable`}>
        Face / Touch ID is not available on this computer — please sign in with your password here. Face login works on iPhone, iPad, Touch ID MacBook and Windows Hello PCs.
      </p>
    );
  }
  if (!supported) return null;
  return (
    <div className="w-full">
    <Button type="button" variant="outline" onClick={() => login(false)} disabled={busy} data-testid={testid}
      className={`w-full h-11 border-2 border-slate-800 text-slate-900 hover:bg-slate-900 hover:text-white font-semibold ${className}`}>
      {busy ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <ScanFace className="w-5 h-5 mr-2" />}
      {busy ? (autoRan ? 'Recognising your face…' : 'Verifying…') : 'Sign in with Face ID / Fingerprint'}
    </Button>
    {registered && autoRan && !busy && (
      <p className="text-[11px] text-center mt-1.5 opacity-80" data-testid={`${testid}-auto-hint`}>Face ID did not open? Tap the button above or sign in with your password</p>
    )}
    {!registered && (
      <p className="text-[11px] text-center mt-1.5 opacity-80" data-testid={`${testid}-hint`}>
        First time here? Sign in with your password — you will be offered Face ID setup right away
      </p>
    )}
    </div>
  );
};

const fmtAt = (iso) => { try { return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch (e) { return iso; } };

/** Recent Face ID logins of the current account — new-device logins highlighted. */
export const PasskeyLoginHistory = ({ logins }) => {
  if (!logins.length) return <p className="text-xs text-slate-400">No Face ID sign-ins yet</p>;
  return (
    <div className="space-y-1" data-testid="passkey-login-history">
      {logins.map((l) => (
        <div key={l.id} className={`flex items-center justify-between text-xs rounded px-2 py-1.5 border ${l.new_device ? 'border-amber-300 bg-amber-50' : 'border-slate-100'}`} data-testid={`passkey-login-${l.id}`}>
          <div className="min-w-0 truncate">
            <p className="font-medium text-slate-700 truncate">
              {l.nickname}
              {l.new_device && <span className="ml-1.5 text-[10px] font-semibold text-amber-700 bg-amber-100 rounded px-1" data-testid={`passkey-login-newdevice-${l.id}`}>NEW DEVICE</span>}
            </p>
            <p className="text-slate-400 truncate" title={l.ua}>{fmtAt(l.at)}{l.ip ? ` · IP ${l.ip}` : ''}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

/** Inside the app: register this device's face/fingerprint + manage registered devices. */
export const PasskeyManager = ({ compact = false }) => {
  const [open, setOpen] = useState(false);
  const [creds, setCreds] = useState([]);
  const [logins, setLogins] = useState([]);
  const [busy, setBusy] = useState(false);
  const [nick, setNick] = useState('');

  const load = useCallback(() => {
    axios.get(`${API}/webauthn/credentials`, cfg()).then((r) => setCreds(r.data)).catch(() => {});
    axios.get(`${API}/webauthn/logins`, cfg()).then((r) => setLogins(r.data)).catch(() => {});
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  const register = async () => {
    setBusy(true);
    try {
      await runRegistration(nick);
      toast.success('Face / device login registered — next time sign in with one tap');
      setNick('');
      load();
    } catch (err) {
      toast.error(friendly(err));
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove face login for this device?')) return;
    try { await axios.delete(`${API}/webauthn/credentials/${id}`, cfg()); forgetCredId(id); load(); toast.success('Device removed'); }
    catch (err) { toast.error(friendly(err)); }
  };

  const [test, setTest] = useState(null);
  const platform = usePlatformAuth();
  const noPlatform = platform === false;
  const runTest = async () => {
    setBusy(true); setTest(null);
    try {
      const { data } = await axios.post(`${API}/webauthn/authentication/options`, { credentialIds: knownCredIds() }, cfg());
      const { transactionId, ...optionsJSON } = data;
      const credential = await startAuthentication({ optionsJSON });
      const res = await axios.post(`${API}/webauthn/authentication/test`, { transactionId, credential }, cfg());
      rememberCredId(credential.id); localStorage.setItem(DEVICE_KEY, '1');
      setTest({ ok: true, msg: `Face ID works on this device (${res.data.nickname}). You can log out safely — next sign-in will be automatic.` });
      load();
    } catch (err) {
      setTest({ ok: false, msg: `Test failed: ${friendly(err)}. Register this device again before logging out.` });
    } finally { setBusy(false); }
  };

  if (!browserSupportsWebAuthn()) return null;
  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="passkey-manage-btn"
        className={`h-9 border-teal-300 text-teal-700 hover:bg-teal-50 ${compact ? 'px-2' : ''}`} title="Face ID / Windows Hello login setup">
        <ScanFace className="w-4 h-4" />{!compact && <span className="ml-1.5">Face Login</span>}
      </Button>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setTest(null); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="passkey-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-teal-600" /> Face / Fingerprint Login</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Register this device's Face ID / Windows Hello / fingerprint — then sign in from the login page with a single tap. With or without glasses, your device recognises you. Your face never leaves your device.</p>
          {noPlatform && (
            <p className="text-xs rounded-md px-3 py-2 border bg-amber-50 border-amber-200 text-amber-800" data-testid="passkey-no-platform">{NO_PLATFORM_MSG}</p>
          )}
          <NicknameInput value={nick} onChange={setNick} testid="passkey-nickname-input" />
          <Button onClick={register} disabled={busy || noPlatform} className="w-full bg-teal-600 hover:bg-teal-700 text-white" data-testid="passkey-register-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ScanFace className="w-4 h-4 mr-2" />} Register this device
          </Button>
          <Button variant="outline" onClick={runTest} disabled={busy || noPlatform || creds.length === 0} data-testid="passkey-test-btn"
            className="w-full border-teal-300 text-teal-700 hover:bg-teal-50" title={creds.length === 0 ? 'Register a device first' : 'Confirm Face ID works here before you log out'}>
            <ShieldCheck className="w-4 h-4 mr-2" /> Test Face ID on this device
          </Button>
          {test && (
            <p className={`text-xs rounded-md px-3 py-2 border ${test.ok ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-red-50 border-red-200 text-red-700'}`} data-testid={`passkey-test-${test.ok ? 'ok' : 'fail'}`}>{test.msg}</p>
          )}
          <div className="border-t pt-3 mt-1">
            <p className="text-xs font-semibold text-slate-500 uppercase mb-1 flex items-center gap-1"><Camera className="w-3.5 h-3.5" /> Face Scan with camera</p>
            <p className="text-[11px] text-slate-500 mb-2">Works on any device with a camera — including computers without Face ID / Touch ID / Windows Hello.</p>
            <FaceEnrol />
          </div>
          <div className="mt-2">
            <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Registered devices ({creds.length})</p>
            {creds.length === 0 && <p className="text-xs text-slate-400">No devices registered yet</p>}
            {creds.map((c) => <CredRow key={c.credential_id} c={c} onRemove={remove} onRenamed={load} />)}
          </div>
          <div className="mt-2 max-h-48 overflow-y-auto">
            <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Recent Face ID sign-ins</p>
            <PasskeyLoginHistory logins={logins} />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

/** Logout safety: true when this browser has no working Face ID / face scan yet (and user hasn't snoozed the reminder). */
export const passkeyRegisteredHere = () => !!(localStorage.getItem(DEVICE_KEY) || knownCredIds().length);
export const needsFaceSetupBeforeLogout = () => {
  if (snoozed()) return false;
  if (platformAvail === true) return !passkeyRegisteredHere();
  return cameraSupported() && !faceDeviceReady();
};

/** Shown instead of logging out immediately when the device has no Face ID yet. */
export const LogoutSafetyDialog = ({ open, onClose, onLogout, portal = false }) => {
  const [busy, setBusy] = useState(false);
  const [nick, setNick] = useState('');
  const setup = async () => {
    setBusy(true);
    try {
      await runRegistration(nick);
      toast.success('Face ID set up — next time you sign in automatically. Logging out…');
      onClose(); onLogout();
    } catch (err) {
      toast.error(friendly(err));
    } finally { setBusy(false); }
  };
  const camMode = platformAvail !== true;
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid="logout-safety-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {camMode ? <Camera className={`w-5 h-5 ${portal ? 'text-orange-500' : 'text-teal-600'}`} /> : <ScanFace className={`w-5 h-5 ${portal ? 'text-orange-500' : 'text-teal-600'}`} />}
            {camMode ? 'Set up camera face login before you leave?' : 'Set up Face ID before you leave?'}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600">
          {camMode
            ? 'This device has no face login yet, so next time you will have to type your password. Scan your face once with the camera (5 seconds) and your next sign-in is automatic.'
            : 'This device has no Face ID / fingerprint login yet, so next time you will have to type your password. Set it up now (takes 5 seconds) and your next sign-in is automatic.'}
        </p>
        {camMode ? (
          <FaceEnrol compact onDone={() => { onClose(); onLogout(); }} />
        ) : (
          <>
            <NicknameInput value={nick} onChange={setNick} testid="logout-safety-nickname-input" />
            <Button onClick={setup} disabled={busy} data-testid="logout-safety-setup-btn"
              className={`w-full text-white ${portal ? 'bg-orange-500 hover:bg-orange-600' : 'bg-teal-600 hover:bg-teal-700'}`}>
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ScanFace className="w-4 h-4 mr-2" />} Set up now, then log out
            </Button>
          </>
        )}
        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={() => { onClose(); onLogout(); }} disabled={busy} data-testid="logout-safety-logout-btn">Log out anyway</Button>
          <Button variant="ghost" className="flex-1 text-slate-500" onClick={onClose} disabled={busy} data-testid="logout-safety-cancel-btn">Cancel</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/** One-time "set up Face ID?" nudge after a password login (mounted globally in App.js). */
export const PasskeySetupPrompt = () => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [nick, setNick] = useState('');
  const [stale, setStale] = useState(false);
  const [mode, setMode] = useState('passkey');
  const [store, setStore] = useState(() => { try { return sessionStorage.getItem(PENDING_KEY); } catch (e) { return null; } });

  useEffect(() => {
    const onPrompt = (e) => setStore(e.detail);
    window.addEventListener('jmepl:passkey-prompt', onPrompt);
    return () => window.removeEventListener('jmepl:passkey-prompt', onPrompt);
  }, []);

  useEffect(() => {
    if (!store) return;
    if (snoozed()) { sessionStorage.removeItem(PENDING_KEY); return; }
    let t;
    const decide = async () => {
      const hasPlatform = browserSupportsWebAuthn() && (await platformReady);
      if (!hasPlatform) {
        // No Face ID / Touch ID / Windows Hello here → offer webcam face scan instead
        if (!cameraSupported() || faceDeviceReady()) { sessionStorage.removeItem(PENDING_KEY); return; }
        try {
          const { data } = await axios.get(`${API}/face/status`, cfg());
          if (data.enrolled && data.device_trusted) { localStorage.setItem('jmepl_face_device_ready', '1'); sessionStorage.removeItem(PENDING_KEY); return; }
        } catch (e) { sessionStorage.removeItem(PENDING_KEY); return; }
        setMode('face');
        t = setTimeout(() => setOpen(true), 1500);
        return;
      }
      if (localStorage.getItem(DEVICE_KEY)) {
        if (knownCredIds().length) { sessionStorage.removeItem(PENDING_KEY); return; }
        try {
          const { data } = await axios.get(`${API}/webauthn/credentials`, cfg());
          if (data.some((c) => !c.needs_reregister)) { sessionStorage.removeItem(PENDING_KEY); return; }
          localStorage.removeItem(DEVICE_KEY);
          setStale(data.length > 0);
        } catch (e) { sessionStorage.removeItem(PENDING_KEY); return; }
      }
      t = setTimeout(() => setOpen(true), 1500);
    };
    decide();
    return () => clearTimeout(t);
  }, [store]);

  const close = (snooze) => {
    sessionStorage.removeItem(PENDING_KEY);
    if (snooze) localStorage.setItem(NEVER_KEY, String(Date.now() + 7 * 24 * 3600 * 1000));
    setOpen(false);
    setStore(null);
  };

  const setup = async () => {
    setBusy(true);
    try {
      await runRegistration(nick);
      toast.success('Face / device login is set — next time sign in with one tap');
      close(false);
    } catch (err) {
      toast.error(friendly(err));
    } finally { setBusy(false); }
  };

  if (!open) return null;
  const portal = store === 'portal';
  if (mode === 'face') {
    return <FaceSetupDialog open={open} portal={portal} onClose={(r) => close(r === 'snooze')} />;
  }
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(false); }}>
      <DialogContent className="max-w-sm" data-testid="passkey-setup-prompt">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ScanFace className={`w-5 h-5 ${portal ? 'text-orange-500' : 'text-teal-600'}`} /> Sign in with Face ID / Fingerprint?</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600">
          {stale
            ? 'Your earlier face login was registered on the old web address, which is why a QR code / another device was being requested. Register once more — after that Face ID / fingerprint will sign you in directly here.'
            : 'No need to type your password next time — this device\'s Face ID / Windows Hello / fingerprint will sign you in with one tap. Your face never leaves your device.'}
        </p>
        <NicknameInput value={nick} onChange={setNick} testid="passkey-prompt-nickname-input" />
        <Button onClick={setup} disabled={busy} data-testid="passkey-prompt-setup-btn"
          className={`w-full text-white ${portal ? 'bg-orange-500 hover:bg-orange-600' : 'bg-teal-600 hover:bg-teal-700'}`}>
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ScanFace className="w-4 h-4 mr-2" />} Set up now
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={() => close(false)} data-testid="passkey-prompt-later-btn">Later</Button>
          <Button variant="ghost" className="flex-1 text-slate-500" onClick={() => close(true)} data-testid="passkey-prompt-never-btn">Ask me in 7 days</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
